import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

const REPORTS = [
  {
    id: 'property-flyer',
    icon: '📄',
    name: 'Property Flyer',
    audience: 'For Marketing',
    audienceCls: 'bg-orange-900/40 text-orange-300 border border-orange-600/30',
    borderCls: 'border-l-4 border-l-[#FF6B35]',
    description:
      'Single-page professional flyer with photos, specs (beds, baths, sqft, schools), price, and agent info. Print or email.',
  },
  {
    id: 'tour-book',
    icon: '📋',
    name: 'Tour Book',
    audience: 'For Buyers',
    audienceCls: 'bg-cyan-900/40 text-cyan-300 border border-cyan-600/30',
    borderCls: 'border-l-4 border-l-[#00D4FF]',
    description:
      "Multi-page printable tour book covering the top 5 homes matched to your buyer's criteria. Take showing notes right on your phone.",
    buyerDescription:
      'Your top 5 matched homes in one tour book. Open it on your phone during showings to take notes on each home.',
    forBuyer: true,
  },
  {
    id: 'comparison',
    icon: '⚖️',
    name: 'Property Comparison',
    audience: 'For Buyers',
    audienceCls: 'bg-green-900/40 text-green-300 border border-green-600/30',
    borderCls: 'border-l-4 border-l-[#00E676]',
    description:
      'Side-by-side comparison of 2-5 homes on key details — price, beds, baths, sqft, schools, fit score.',
    buyerDescription:
      'Pick 2-5 of your matched homes and see them side by side — price, beds, baths, sqft, schools, fit score.',
    forBuyer: true,
  },
  {
    id: 'match-summary',
    icon: '📊',
    name: 'Match Summary',
    audience: 'For Buyers',
    audienceCls: 'bg-purple-900/40 text-purple-300 border border-purple-600/30',
    borderCls: 'border-l-4 border-l-[#9C27B0]',
    description:
      'Executive summary of every home scored for a buyer, ranked by fit score, with their criteria weights.',
  },
  {
    id: 'listing-interest',
    icon: '🏡',
    name: 'Listing Interest',
    audience: 'For Sellers',
    audienceCls: 'bg-yellow-900/40 text-yellow-300 border border-yellow-600/30',
    borderCls: 'border-l-4 border-l-[#FFC107]',
    description:
      "Sell-side report showing every buyer who scored this home and why they're interested.",
  },
];

const inputCls =
  'w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]';
const btnCls =
  'px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm';

const formatPrice = (price) =>
  price != null ? `$${Number(price).toLocaleString()}` : null;

const getScorePercent = (score) => {
  if (!score) return 0;
  return Math.round((score / 9) * 100);
};

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedReport, setSelectedReport] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Form state
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedBuyerId, setSelectedBuyerId] = useState('');
  const [boardEntries, setBoardEntries] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [comparisonIds, setComparisonIds] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Reports are an agent/broker tool; buyers get the buyer-facing subset (Tour Book,
  // Property Comparison) for their own record only. Any other role cannot use this page.
  const isBuyer = user?.role === 'buyer' && !!user?.buyer_id;
  const canView = isBuyer || ['super_admin', 'broker', 'agent'].includes(user?.role);
  const visibleReports = isBuyer ? REPORTS.filter((r) => r.forBuyer) : REPORTS;
  const describe = (r) => (isBuyer && r.buyerDescription) || r.description;

  useEffect(() => {
    if (!canView) {
      setLoadingData(false);
      return;
    }
    if (isBuyer) {
      // A buyer reports only on themself — no buyer/home pickers to load.
      setBuyers([{ id: user.buyer_id, name: user.full_name || 'Me' }]);
      setLoadingData(false);
      return;
    }
    const loadAll = async () => {
      try {
        const [buyersRes, propsRes] = await Promise.all([
          authFetch(`${API}/api/buyers`),
          authFetch(`${API}/api/properties?limit=200`),
        ]);
        if (buyersRes.ok) {
          const data = await buyersRes.json();
          setBuyers(Array.isArray(data) ? data : []);
        }
        if (propsRes.ok) {
          const data = await propsRes.json();
          setProperties(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Reports load error:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadAll();
  }, [canView, isBuyer, user?.buyer_id, user?.full_name]);

  // When buyer changes for comparison, load their match board so the agent can
  // pick 2-5 of that buyer's scored homes.
  useEffect(() => {
    const needsBoard = selectedReport === 'comparison';
    if (!needsBoard || !selectedBuyerId) {
      setBoardEntries([]);
      setComparisonIds([]);
      return;
    }
    let cancelled = false;
    const loadBoard = async () => {
      setBoardLoading(true);
      try {
        const res = await authFetch(`${API}/api/board/buyer/${selectedBuyerId}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setBoardEntries(Array.isArray(data) ? data : []);
          setComparisonIds([]);
        }
      } catch (err) {
        console.error('Load board error:', err);
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    };
    loadBoard();
    return () => { cancelled = true; };
  }, [selectedReport, selectedBuyerId]);

  const resetForm = () => {
    setSelectedPropertyId('');
    setSelectedBuyerId(isBuyer ? String(user.buyer_id) : '');
    setBoardEntries([]);
    setComparisonIds([]);
    setError('');
  };

  const handleSelectReport = (id) => {
    if (selectedReport === id) {
      setSelectedReport(null);
      resetForm();
    } else {
      setSelectedReport(id);
      resetForm();
    }
  };

  const toggleComparisonId = (pid) => {
    setComparisonIds((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length >= 5) return prev;
      return [...prev, pid];
    });
  };

  const handleGenerate = () => {
    setError('');
    let params = null;
    if (selectedReport === 'property-flyer') {
      if (!selectedPropertyId) {
        setError('Please choose a home.');
        return;
      }
      params = new URLSearchParams({ type: 'flyer', property_id: selectedPropertyId });
    } else if (selectedReport === 'tour-book') {
      if (!selectedBuyerId) {
        setError('Please choose a buyer.');
        return;
      }
      params = new URLSearchParams({ type: 'tour-book', buyer_id: selectedBuyerId });
    } else if (selectedReport === 'comparison') {
      if (!selectedBuyerId) {
        setError('Please choose a buyer.');
        return;
      }
      if (comparisonIds.length < 2 || comparisonIds.length > 5) {
        setError('Pick between 2 and 5 homes to compare.');
        return;
      }
      params = new URLSearchParams({
        type: 'comparison',
        buyer_id: selectedBuyerId,
        property_ids: comparisonIds.join(','),
      });
    } else if (selectedReport === 'match-summary') {
      if (!selectedBuyerId) {
        setError('Please choose a buyer.');
        return;
      }
      params = new URLSearchParams({ type: 'match-summary', buyer_id: selectedBuyerId });
    } else if (selectedReport === 'listing-interest') {
      if (!selectedPropertyId) {
        setError('Please choose a home.');
        return;
      }
      params = new URLSearchParams({ type: 'listing-interest', property_id: selectedPropertyId });
    } else {
      return;
    }

    setGenerating(true);
    try {
      navigate(`/reports/view?${params.toString()}`);
    } finally {
      setGenerating(false);
    }
  };

  const renderPropertyOptions = () =>
    properties.map((p) => {
      const priceLabel = formatPrice(p.price);
      const label = [
        p.address || '(no address)',
        p.city ? `· ${p.city}${p.state ? ', ' + p.state : ''}` : null,
        priceLabel ? `· ${priceLabel}` : null,
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <option key={p.id} value={p.id}>
          {label}
        </option>
      );
    });

  const renderBuyerOptions = () =>
    buyers.map((b) => (
      <option key={b.id} value={b.id}>
        {b.name}
      </option>
    ));

  const renderConfigPanel = () => {
    if (!selectedReport) return null;
    const report = REPORTS.find((r) => r.id === selectedReport);
    if (!report) return null;

    return (
      <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-5 mt-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-white font-bold text-lg flex items-center gap-2">
              <span className="text-2xl">{report.icon}</span>
              {report.name}
            </h2>
            <p className="text-[#B0BEC5] text-sm mt-1">{describe(report)}</p>
          </div>
          <button
            onClick={() => { setSelectedReport(null); resetForm(); }}
            className="text-gray-400 hover:text-white text-sm"
            aria-label="Close report config"
          >
            Close
          </button>
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-600/40 text-red-300 text-sm rounded-lg px-3 py-2 mb-4 flex items-start justify-between gap-3">
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-300 hover:text-white text-xs px-2 py-0.5 border border-red-600/40 rounded"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Property Flyer */}
        {selectedReport === 'property-flyer' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#B0BEC5] mb-1">Home</label>
              <select
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                className={inputCls}
              >
                <option value="">Choose a home...</option>
                {renderPropertyOptions()}
              </select>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedPropertyId}
              className={btnCls}
            >
              {generating ? 'Generating…' : 'Generate Flyer'}
            </button>
          </div>
        )}

        {/* Tour Book */}
        {selectedReport === 'tour-book' && (
          <div className="space-y-4">
            {!isBuyer && (
              <div>
                <label className="block text-xs font-medium text-[#B0BEC5] mb-1">Buyer</label>
                <select
                  value={selectedBuyerId}
                  onChange={(e) => setSelectedBuyerId(e.target.value)}
                  className={inputCls}
                >
                  {!selectedBuyerId && <option value="">Choose a buyer...</option>}
                  {renderBuyerOptions()}
                </select>
              </div>
            )}
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedBuyerId}
              className={btnCls}
            >
              {generating ? 'Generating…' : 'Generate Tour Book'}
            </button>
          </div>
        )}

        {/* Comparison */}
        {selectedReport === 'comparison' && (
          <div className="space-y-4">
            {!isBuyer && (
              <div>
                <label className="block text-xs font-medium text-[#B0BEC5] mb-1">Buyer</label>
                <select
                  value={selectedBuyerId}
                  onChange={(e) => setSelectedBuyerId(e.target.value)}
                  className={inputCls}
                >
                  {!selectedBuyerId && <option value="">Choose a buyer...</option>}
                  {renderBuyerOptions()}
                </select>
              </div>
            )}

            {selectedBuyerId && (
              <div>
                <label className="block text-xs font-medium text-[#B0BEC5] mb-1">
                  Pick 2–5 homes to compare
                  <span className="ml-2 text-gray-500">({comparisonIds.length} selected)</span>
                </label>
                {boardLoading ? (
                  <div className="text-[#B0BEC5] text-sm py-3">Loading match board…</div>
                ) : boardEntries.length === 0 ? (
                  <div className="text-gray-400 text-sm py-3 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg px-3">
                    No scored homes for this buyer yet.
                  </div>
                ) : (
                  <div className="bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg max-h-72 overflow-y-auto divide-y divide-[#546E7A]/20">
                    {boardEntries.map((entry) => {
                      const pid = entry.property_id;
                      const pct = getScorePercent(entry.fit_score);
                      const checked = comparisonIds.includes(pid);
                      const disabled = !checked && comparisonIds.length >= 5;
                      return (
                        <label
                          key={pid}
                          className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[#1A2744] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleComparisonId(pid)}
                            className="w-4 h-4 accent-[#00D4FF]"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-white truncate">{entry.address || '(no address)'}</div>
                            <div className="text-xs text-gray-400">
                              {entry.city ? `${entry.city}${entry.state ? ', ' + entry.state : ''}` : ''}
                            </div>
                          </div>
                          <div className="text-[#00D4FF] font-semibold text-sm">{pct}%</div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={
                generating ||
                !selectedBuyerId ||
                comparisonIds.length < 2 ||
                comparisonIds.length > 5
              }
              className={btnCls}
            >
              {generating ? 'Generating…' : 'Generate Comparison'}
            </button>
          </div>
        )}

        {/* Match Summary */}
        {selectedReport === 'match-summary' && (
          <div className="space-y-4">
            {!isBuyer && (
              <div>
                <label className="block text-xs font-medium text-[#B0BEC5] mb-1">Buyer</label>
                <select
                  value={selectedBuyerId}
                  onChange={(e) => setSelectedBuyerId(e.target.value)}
                  className={inputCls}
                >
                  {!selectedBuyerId && <option value="">Choose a buyer...</option>}
                  {renderBuyerOptions()}
                </select>
              </div>
            )}
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedBuyerId}
              className={btnCls}
            >
              {generating ? 'Generating…' : 'Generate Summary'}
            </button>
          </div>
        )}

        {/* Listing Interest */}
        {selectedReport === 'listing-interest' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#B0BEC5] mb-1">Home</label>
              <select
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                className={inputCls}
              >
                <option value="">Choose a home...</option>
                {renderPropertyOptions()}
              </select>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedPropertyId}
              className={btnCls}
            >
              {generating ? 'Generating…' : 'Generate Report'}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <h1 className="text-white text-2xl font-bold mb-2">Reports</h1>
            <p className="text-[#B0BEC5]">This page is not available for your account.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-white">Reports</h1>
          <p className="text-[#B0BEC5] text-sm mt-1">
            {isBuyer
              ? 'Tour your top homes and compare your favorites side by side'
              : 'Generate professional reports from your home and buyer data'}
          </p>
        </div>

        {loadingData ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-[#00D4FF] text-lg">Loading...</div>
          </div>
        ) : buyers.length === 0 && properties.length === 0 ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16 px-6">
            <p className="text-[#B0BEC5] text-lg mb-2">No data to report on yet</p>
            <p className="text-gray-500 mb-4">
              Add at least one buyer and one home before generating reports.
            </p>
            <div className="flex gap-3 justify-center">
              <a
                href="/buyers"
                className="px-5 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors text-sm"
              >
                Add Buyers
              </a>
              <a
                href="/properties"
                className="px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] transition-colors text-sm"
              >
                Add Homes
              </a>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {visibleReports.map((r) => {
                const isSelected = selectedReport === r.id;
                return (
                  <div
                    key={r.id}
                    onClick={() => handleSelectReport(r.id)}
                    className={`bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 cursor-pointer hover:border-[#00D4FF]/40 transition-colors ${r.borderCls} ${isSelected ? 'ring-2 ring-[#00D4FF]/40' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-4xl leading-none">{r.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="text-white font-bold text-base">{r.name}</h3>
                          {!isBuyer && (
                            <span className={`px-2 py-0.5 rounded text-xs ${r.audienceCls}`}>
                              {r.audience}
                            </span>
                          )}
                        </div>
                        <p className="text-[#B0BEC5] text-sm leading-snug">{describe(r)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {renderConfigPanel()}
          </>
        )}
      </div>
    </div>
  );
}
